import React, { useState, useEffect } from 'react';
import { UserPlus, Tag, Ticket, Check, Loader2, X, Plus, Sparkles, ChefHat } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { TagsEditor } from './TagsEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const relTime = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const secs = Math.floor((Date.now() - then) / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
};


// ============================================================================
// Customer review queue — admin dashboard tab.
// Shows every new signup that hasn't been "reviewed" yet, so the shop owner
// can add tags / issue welcome vouchers and click Done to clear them off the
// list. Signups appear here automatically the moment they register.
// ============================================================================

export const NewSignupsAdmin = () => {
  const [rows, setRows] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [wheelPrizes, setWheelPrizes] = useState([]);
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);

  const fetchQueue = async () => {
    try {
      const res = await axios.get(`${API}/admin/customer-review/queue`, { headers: authHeaders() });
      setRows(res.data);
    } catch {
      toast.error('Could not load review queue');
    }
  };

  useEffect(() => {
    fetchQueue();
    // Also pull the current Ted's Wheel prizes so admins can grant them
    // with one click instead of retyping the label/code/description.
    axios.get(`${API}/admin/teds-wheel`, { headers: authHeaders() })
      .then(res => setWheelPrizes((res.data?.prizes || []).filter(p => p.code)))
      .catch(() => setWheelPrizes([]));
    const t = setInterval(fetchQueue, 30000);
    return () => clearInterval(t);
  }, []);

  const runTedsTableMigration = async () => {
    if (!window.confirm('Re-open every Ted\u2019s Table customer for review and add the teds_table tag? This is safe to run more than once.')) return;
    setMigrating(true);
    setMigrationResult(null);
    try {
      const res = await axios.post(`${API}/admin/customer-review/migrate-teds-table`, {}, { headers: authHeaders() });
      setMigrationResult(res.data);
      toast.success(`Migration done — ${res.data.queued} customers re-queued`);
      fetchQueue();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Migration failed');
    } finally {
      setMigrating(false);
    }
  };

  if (!rows) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-gray-300 animate-spin" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="new-signups-admin">
      <div className="bg-gradient-to-r from-[#1f6d62] to-[#0FA49C] rounded-2xl p-5 text-white shadow">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
            <UserPlus className="w-5 h-5 text-[#a0da39]" />
          </div>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-widest text-white/70 font-bold">New signups</p>
            <h2 className="text-2xl font-black">Review queue</h2>
            <p className="text-sm text-white/80 mt-0.5">Add tags or vouchers to fresh accounts, then hit Done to clear them.</p>
          </div>
          <div className="text-right">
            <p className="text-4xl font-black" data-testid="review-queue-count">{rows.length}</p>
            <p className="text-[10px] uppercase tracking-widest text-white/70 font-bold">awaiting</p>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-white/15 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-white/80 text-sm">
            <ChefHat className="w-4 h-4 text-[#a0da39]" />
            <span>Bring existing <strong>Ted&apos;s Table</strong> customers back to this queue for review.</span>
          </div>
          <Button
            onClick={runTedsTableMigration}
            disabled={migrating}
            size="sm"
            className="bg-[#a0da39] hover:bg-[#b4e856] text-[#1f3d2f] font-bold"
            data-testid="teds-table-migration-btn"
          >
            {migrating ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Migrating…</> : <><ChefHat className="w-4 h-4 mr-1.5" /> Migrate Ted&apos;s Table customers</>}
          </Button>
        </div>
        {migrationResult && (
          <div className="mt-3 bg-white/10 rounded-lg p-3 text-xs" data-testid="teds-table-migration-result">
            <p className="font-bold text-[#a0da39] mb-1">Migration complete</p>
            <ul className="space-y-0.5 text-white/85">
              <li><strong>{migrationResult.linked}</strong> customers linked (union of paying + already-tagged)</li>
              <li><strong>{migrationResult.tagged}</strong> newly tagged with <code className="bg-white/15 px-1 rounded">teds_table</code></li>
              <li><strong>{migrationResult.queued}</strong> re-opened for review</li>
              {migrationResult.guests_without_account_count > 0 && (
                <li>
                  <strong>{migrationResult.guests_without_account_count}</strong> guest orders with no linked account —{' '}
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigator.clipboard.writeText((migrationResult.guests_without_account || []).map(g => g.email).join(', '));
                      toast.success('Guest emails copied');
                    }}
                    className="underline text-[#a0da39]"
                  >
                    copy their emails
                  </a>
                </li>
              )}
            </ul>
          </div>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-16 text-center">
          <Check className="w-10 h-10 mx-auto text-emerald-400 mb-2" />
          <p className="text-sm text-[#666] font-semibold">All caught up</p>
          <p className="text-xs text-[#999] mt-0.5">New signups will appear here automatically.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map(c => (
            <SignupRow
              key={c.customer_id}
              customer={c}
              expanded={expanded === c.customer_id}
              onToggle={() => setExpanded(prev => prev === c.customer_id ? null : c.customer_id)}
              onDone={fetchQueue}
              wheelPrizes={wheelPrizes}
            />
          ))}
        </div>
      )}
    </div>
  );
};


// ============================================================================
// Single signup row — collapsible. Header always visible, body appears when
// expanded so long queues stay scannable.
// ============================================================================

const SignupRow = ({ customer, expanded, onToggle, onDone, wheelPrizes }) => {
  const [marking, setMarking] = useState(false);
  const [vouchers, setVouchers] = useState(customer.vouchers || []);

  const markDone = async () => {
    setMarking(true);
    try {
      await axios.post(`${API}/admin/customers/${customer.customer_id}/mark-reviewed`, {}, { headers: authHeaders() });
      toast.success(`${customer.name || customer.email} reviewed`);
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not mark reviewed');
    } finally {
      setMarking(false);
    }
  };

  const revoke = async (voucherId) => {
    if (!window.confirm('Revoke this voucher?')) return;
    try {
      await axios.delete(`${API}/admin/customers/${customer.customer_id}/vouchers/${voucherId}`, { headers: authHeaders() });
      setVouchers(prev => prev.filter(v => v.id !== voucherId));
      toast.success('Voucher revoked');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not revoke');
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`signup-row-${customer.customer_id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-50"
        data-testid={`signup-row-toggle-${customer.customer_id}`}
      >
        <div className="w-10 h-10 rounded-full bg-[#0FA49C]/10 flex items-center justify-center text-sm font-black text-[#0FA49C] flex-shrink-0">
          {(customer.name || customer.email).slice(0, 1).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-[#1a1a1a] truncate">{customer.name || '—'}</p>
          <p className="text-xs text-[#666] truncate">{customer.email}</p>
        </div>
        <div className="hidden sm:block text-right flex-shrink-0">
          <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">Signed up</p>
          <p className="text-xs text-[#666]">{relTime(customer.created_at)}</p>
        </div>
        {customer.signup_source && customer.signup_source !== 'web' && (
          <span className="hidden md:inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-[#1f6d62]/10 text-[#1f6d62] px-2 py-1 rounded-full">
            <Sparkles className="w-3 h-3" /> {customer.signup_source.replace('_', ' ')}
          </span>
        )}
      </button>

      {expanded && (
        <div className="border-t border-gray-100 p-4 space-y-4 bg-gray-50/50">
          {/* Tags */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 flex items-center gap-1.5 mb-2">
              <Tag className="w-3 h-3" /> Tags
            </label>
            <TagsEditor
              customerId={customer.customer_id}
              initialTags={customer.tags || []}
              dense
            />
          </div>

          {/* Vouchers */}
          <VoucherGranter
            customerId={customer.customer_id}
            existing={vouchers}
            onAdded={(v) => setVouchers(prev => [...prev, v])}
            onRevoke={revoke}
            wheelPrizes={wheelPrizes}
          />

          {/* Done */}
          <div className="flex justify-end pt-1">
            <Button
              onClick={markDone}
              disabled={marking}
              className="bg-[#1f6d62] hover:bg-[#14544b] text-white"
              data-testid={`signup-done-${customer.customer_id}`}
            >
              {marking ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : <><Check className="w-4 h-4 mr-1.5" /> Done — clear from list</>}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};


// ============================================================================
// Voucher granter — add a welcome/goodwill voucher to the customer's
// Vouchers section on their /account page.
// ============================================================================

const VoucherGranter = ({ customerId, existing, onAdded, onRevoke, wheelPrizes = [] }) => {
  const [label, setLabel] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [claimAt, setClaimAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [grantingPrize, setGrantingPrize] = useState(null);

  const persist = async ({ label, code, description, claim_at, source }) => {
    const res = await axios.post(
      `${API}/admin/customers/${customerId}/vouchers`,
      { label, code: code || null, description: description || null, claim_at: claim_at || null, source: source || 'admin' },
      { headers: authHeaders() },
    );
    onAdded?.(res.data);
    return res.data;
  };

  const add = async () => {
    if (!label.trim()) {
      toast.error('Give the voucher a label');
      return;
    }
    setBusy(true);
    try {
      await persist({ label: label.trim(), code: code.trim(), description: description.trim(), claim_at: claimAt.trim() });
      setLabel(''); setCode(''); setDescription(''); setClaimAt('');
      toast.success('Voucher added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add voucher');
    } finally {
      setBusy(false);
    }
  };

  const grantWheelPrize = async (prize) => {
    setGrantingPrize(prize.id);
    try {
      await persist({
        label: prize.label,
        code: prize.code || '',
        description: prize.description || '',
        claim_at: prize.claim_at || '',
        source: 'wheel',
      });
      toast.success(`Granted ${prize.label}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not grant voucher');
    } finally {
      setGrantingPrize(null);
    }
  };

  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 flex items-center gap-1.5 mb-2">
        <Ticket className="w-3 h-3" /> Vouchers
      </label>
      {existing.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {existing.map(v => (
            <div key={v.id} className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 px-3 py-2 text-sm">
              <div className="flex-1 min-w-0">
                <p className="font-bold text-[#1a1a1a] truncate">{v.label}</p>
                {v.description && <p className="text-[11px] text-[#888] truncate">{v.description}</p>}
                {v.claim_at && (
                  <p className="text-[10px] text-[#0FA49C] font-bold uppercase tracking-widest truncate">
                    Claim at {v.claim_at.replace(/^https?:\/\//, '')}
                  </p>
                )}
              </div>
              {v.code && (
                <span className="font-mono text-xs bg-[#a0da39] text-[#1f3d2f] font-black px-2 py-1 rounded tracking-widest">{v.code}</span>
              )}
              <button
                type="button"
                onClick={() => onRevoke(v.id)}
                className="text-gray-400 hover:text-red-500 p-1"
                title="Revoke voucher"
                data-testid={`signup-voucher-revoke-${v.id}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Ted's Wheel quick-add — one click grants a pre-configured wheel prize */}
      {wheelPrizes.length > 0 && (
        <div className="mb-3">
          <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 flex items-center gap-1.5 mb-1.5">
            <Sparkles className="w-3 h-3 text-[#a0da39]" /> Grant from Ted&apos;s Wheel
          </p>
          <div className="flex flex-wrap gap-1.5" data-testid={`signup-wheel-prizes-${customerId}`}>
            {wheelPrizes.map(p => {
              const alreadyGranted = existing.some(v => (v.code || '').toUpperCase() === (p.code || '').toUpperCase());
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => grantWheelPrize(p)}
                  disabled={alreadyGranted || grantingPrize === p.id}
                  title={p.description || p.label}
                  className={`inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-2.5 py-1.5 border transition-colors ${
                    alreadyGranted
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'bg-white text-[#1f6d62] border-[#1f6d62]/30 hover:bg-[#1f6d62] hover:text-white hover:border-[#1f6d62]'
                  }`}
                  data-testid={`signup-wheel-prize-${customerId}-${p.id}`}
                >
                  {grantingPrize === p.id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : alreadyGranted ? (
                    <Check className="w-3 h-3" />
                  ) : (
                    <Plus className="w-3 h-3" />
                  )}
                  <span>{p.label}</span>
                  {p.code && (
                    <span className="font-mono text-[10px] opacity-70 tracking-widest">{p.code}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5">Or add a custom voucher</p>
      <div className="grid grid-cols-12 gap-2">
        <div className="col-span-12 sm:col-span-4">
          <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (e.g. 20% welcome)" data-testid={`signup-voucher-label-${customerId}`} />
        </div>
        <div className="col-span-6 sm:col-span-3">
          <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="CODE" className="font-mono uppercase" data-testid={`signup-voucher-code-${customerId}`} />
        </div>
        <div className="col-span-12 sm:col-span-4">
          <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short description" data-testid={`signup-voucher-desc-${customerId}`} />
        </div>
        <div className="col-span-6 sm:col-span-1">
          <Button onClick={add} disabled={busy} className="w-full bg-[#a0da39] hover:bg-[#b4e856] text-[#1f3d2f]" data-testid={`signup-voucher-add-${customerId}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </Button>
        </div>
        <div className="col-span-12">
          <Input
            value={claimAt}
            onChange={(e) => setClaimAt(e.target.value)}
            placeholder="Claim at (e.g. https://ultimatepetfoods.co.uk or In-store)"
            data-testid={`signup-voucher-claimat-${customerId}`}
          />
        </div>
      </div>
    </div>
  );
};

export default NewSignupsAdmin;
